import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Zap, Wallet, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { ServiceCard } from "../components/ServiceCard";
import { DoctorCard } from "../components/DoctorCard";
import { ServiceAreaMap } from "../components/ServiceAreaMap";
import { Testimonials } from "../components/Testimonials";
import { useLang } from "../context/LanguageContext";
import { api, waLink } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const { lang, t } = useLang();
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/doctors").then((r) => setDoctors(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const whyItems = [
    { icon: ShieldCheck, tk: "why.1t", dk: "why.1d" },
    { icon: Zap, tk: "why.2t", dk: "why.2d" },
    { icon: Wallet, tk: "why.3t", dk: "why.3d" },
    { icon: MessageCircle, tk: "why.4t", dk: "why.4d" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-20 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-3 animate-fade-up">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 bg-emerald-50 px-4 py-1.5 rounded-full mb-6">{t("hero.badge")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mb-6">{t("hero.title")}</h1>
          <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-8 max-w-xl">{t("hero.subtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" data-testid="hero-book-btn" className="rounded-full px-8 h-12 text-base hover:scale-105 transition-transform">
              <Link to="/booking">{t("hero.cta_book")} <ArrowRight className="w-4 h-4 ml-1" /></Link>
            </Button>
            <a href={waLink(lang === "ms" ? "Hai, saya ingin menempah lawatan doktor ke rumah." : "Hi, I would like to book a home doctor visit.")}
              target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[#25D366] text-white font-semibold text-base hover:scale-105 transition-transform">
              <MessageCircle className="w-5 h-5" /> {t("hero.cta_wa")}
            </a>
          </div>
          <div className="flex gap-10 mt-12">
            <div><p className="font-heading text-3xl font-semibold text-primary">2,500+</p><p className="text-sm text-slate-500">{t("hero.stat_patients")}</p></div>
            <div><p className="font-heading text-3xl font-semibold text-primary">10+</p><p className="text-sm text-slate-500">{t("hero.stat_exp")}</p></div>
            <div><p className="font-heading text-3xl font-semibold text-primary">20+</p><p className="text-sm text-slate-500">{t("hero.stat_areas")}</p></div>
          </div>
        </div>
        <div className="lg:col-span-2 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <img src={HERO_IMG} alt="Hospital waiting area" className="rounded-2xl w-full h-[420px] lg:h-[520px] object-cover shadow-md" />
        </div>
      </section>

      {/* Services */}
      <section className="bg-white border-y border-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-3">{t("services.title")}</h2>
          <p className="text-base md:text-lg text-slate-500 mb-12">{t("services.subtitle")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => <ServiceCard key={s.id} service={s} featured={i === 0} />)}
          </div>
          <div className="mt-10">
            <Link to="/services" data-testid="home-viewall-services" className="inline-flex items-center gap-2 font-semibold text-primary hover:gap-3 transition-[gap]">
              {t("services.viewall")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-3">{t("why.title")}</h2>
        <p className="text-base md:text-lg text-slate-500 mb-12">{t("why.subtitle")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyItems.map(({ icon: Icon, tk, dk }) => (
            <div key={tk} className="bg-white rounded-2xl border border-slate-200 p-8">
              <span className="inline-flex w-12 h-12 rounded-xl bg-emerald-50 items-center justify-center mb-5">
                <Icon className="w-6 h-6 text-emerald-600" />
              </span>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{t(tk)}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{t(dk)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Doctors preview */}
      <section className="bg-white border-y border-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-3">{t("doctors.title")}</h2>
          <p className="text-base md:text-lg text-slate-500 mb-12">{t("doctors.subtitle")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.map((d) => <DoctorCard key={d.id} doctor={d} />)}
          </div>
          <div className="mt-10">
            <Link to="/doctors" data-testid="home-viewall-doctors" className="inline-flex items-center gap-2 font-semibold text-primary hover:gap-3 transition-[gap]">
              {t("doctors.viewall")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Map */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-3">{t("map.title")}</h2>
        <p className="text-base md:text-lg text-slate-500 mb-10">{t("map.subtitle")}</p>
        <ServiceAreaMap />
      </section>
    </div>
  );
}
